import React from 'react';
import { AxisFormat } from './ChartRenderer';

interface KpiTilesProps {
  data: Record<string, unknown>[];
  xColumn: string;
  yColumn: string;
  title?: string;
  columnOrder?: string[];
  valueFormat?: AxisFormat;
  tileFormats?: Record<string, AxisFormat>;
}

function mapFormatCode(code: string): AxisFormat | undefined {
  const map: Record<string, AxisFormat> = {
    'usd': { format_type: 'currency', prefix: '$' },
    'currency_usd': { format_type: 'currency', prefix: '$' },
    'currency_usd_k': { format_type: 'currency', prefix: '$', suffix: 'K', decimal_places: 1 },
    'currency_usd_m': { format_type: 'currency', prefix: '$', suffix: 'M', decimal_places: 1 },
    'number': { format_type: 'number' },
    'number_k': { format_type: 'number', suffix: 'K', decimal_places: 1 },
    'number_m': { format_type: 'number', suffix: 'M', decimal_places: 1 },
    'percent': { format_type: 'percent' },
  };
  return map[code.toLowerCase()];
}

const KpiTiles: React.FC<KpiTilesProps> = ({ data, xColumn, yColumn, title, columnOrder, valueFormat, tileFormats }) => {
  if (!data || data.length === 0) return null;

  const formatValue = (val: unknown, fmt?: AxisFormat): string => {
    if (val === null || val === undefined) return '-';
    const num = typeof val === 'number' ? val : parseFloat(String(val));
    if (isNaN(num)) return String(val);

    // Use provided format if available
    if (fmt) {
      const prefix = fmt.prefix || '';
      const suffix = fmt.suffix || '';
      let scaledValue = num;
      if (suffix === 'K') scaledValue = num / 1000;
      else if (suffix === 'M') scaledValue = num / 1000000;

      const decimals = fmt.decimal_places ?? (scaledValue % 1 !== 0 ? 2 : 0);
      const formatted = scaledValue.toLocaleString('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });
      return `${prefix}${formatted}${suffix}`;
    }

    // Default auto-formatting
    if (Math.abs(num) >= 1000000) {
      return `$${(num / 1000000).toFixed(1)}M`;
    } else if (Math.abs(num) >= 1000) {
      return num.toLocaleString('en-US', { maximumFractionDigits: 0 });
    } else if (num % 1 !== 0) {
      return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    return num.toLocaleString('en-US');
  };

  const formatLabel = (key: string): string => {
    return key
      .replace(/_/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
  };

  // Uniform color for all tiles
  const tileColor = { bg: 'rgba(99, 102, 241, 0.1)', border: 'rgba(99, 102, 241, 0.3)', value: '#818cf8' };

  // Determine tile data: if 1 row with multiple columns, transpose (each column becomes a tile)
  // Otherwise use standard label/value row format
  let tiles: { label: string; key: string; value: unknown; format?: string }[];

  if (data.length === 1 && Object.keys(data[0]).length >= 2) {
    // Transpose: each column becomes a tile
    tiles = Object.entries(data[0]).map(([key, val]) => ({
      label: formatLabel(key),
      key, // preserve original column name for tileFormats lookup
      value: val,
    }));
  } else {
    // Standard: each row is a tile with xColumn as label, yColumn as value
    tiles = data.slice(0, 6).map(row => ({
      label: String(row[xColumn] || ''),
      key: String(row[xColumn] || ''),
      value: row[yColumn],
      format: typeof row['format'] === 'string' ? row['format'] : undefined,
    }));
    // Exclude tiles whose label is 'Format' (case-insensitive)
    tiles = tiles.filter(t => t.label.toLowerCase() !== 'format');
  }

  // Apply custom column ordering if provided
  if (columnOrder && columnOrder.length > 0) {
    const orderLower = columnOrder.map(o => o.toLowerCase());
    const ordered: typeof tiles = [];
    const remaining = [...tiles];

    for (const name of orderLower) {
      const idx = remaining.findIndex(t =>
        t.label.toLowerCase() === name ||
        t.label.toLowerCase().replace(/\s+/g, '_') === name ||
        name.replace(/_/g, ' ') === t.label.toLowerCase()
      );
      if (idx !== -1) {
        ordered.push(remaining.splice(idx, 1)[0]);
      }
    }
    // Append any tiles not in the order list
    tiles = [...ordered, ...remaining];
  }

  return (
    <div className="kpi-tiles-wrapper">
      {title && <div className="kpi-tiles-title">{title}</div>}
      <div className="kpi-tiles-container">
        {tiles.slice(0, 6).map((tile, i) => {
          const color = tileColor;
          // Resolve format: config tileFormats > row format > global valueFormat > auto-detect
          const tileFormat = tileFormats?.[tile.key]
            || tileFormats?.[tile.label]
            || tileFormats?.[tile.label.toLowerCase().replace(/\s+/g, '_')]
            || (tile.format ? mapFormatCode(tile.format) : undefined)
            || valueFormat;
          return (
            <div
              key={i}
              className="kpi-tile"
              style={{
                background: color.bg,
                borderColor: color.border,
              }}
            >
              <div className="kpi-tile-label">{tile.label}</div>
              <div className="kpi-tile-value" style={{ color: color.value }}>
                {formatValue(tile.value, tileFormat)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default KpiTiles;
